// helpers
import { bindEvents, getRootOffsetBounds, lodash, mq, multiAddEventListener, multiRemoveEventListener } from '../../assets/js/helpers';

// controllers
import AnimationController from '../../assets/js/controller/animation';
import CursorController from '../../assets/js/controller/cursor';
import ResizeController from '../../assets/js/controller/resize';

// bits
import CursorTrailing from '../../bits/cursors/trailing/script';

// plugins
import SplitText from '../../assets/js/plugins/SplitText';

// dom selector
const selector = 'guest-list';

export default class GuestList {

	constructor(section) {
		this.el = {
			section,

			cursor: section.querySelector(`.${selector}__rollover-cursor`),
			cursorImages: section.querySelectorAll(`.${selector}__rollover-cursor-images-wrapper > *`),

			items: section.querySelectorAll(`.${selector}__item`),
			itemsButtons: section.querySelectorAll(`.${selector}__guest-name`),

			activeItem: null,
			activeCursorImage: null
		};

		// class variable groups

		this.bounds = {
			section: null,
			activeItem: null
		};

		this.controllers = {
			animateCursor: null,
			resize: null
		};

		this.coords = {
			activeItem: null
		};

		this.flags = {
			isActive: false,
			isCursorOver: false,
			isCursorActive: false,
			isInteractionReady: true
		};

		this.throttled = {
			resize: lodash.throttle(this.measureResize.bind(this), 500, { leading: false, trailing: true })
		};

		// class variables
		this.splits = [];
		this.cursor = new CursorTrailing(this.el.cursor);

		// bind events to class scope
		this.events = bindEvents(this);

		// engage
		this.init();
	}

	static get selector() {
		return selector;
	}

	// initialize class

	init() {
		this.addEventListeners();
	}

	// event listeners

	addEventListeners() {
		multiAddEventListener(this.el.itemsButtons, 'mouseenter', this.events._onItemEnter);
		multiAddEventListener(this.el.items, 'mouseleave', this.events._onItemLeave);

		this.controllers.resize = ResizeController.set({
			update: this.events._onResizeUpdate,
			resize: () => {}
		});

		this.measureResize();
		this.cursor.start();
	}

	removeEventListeners() {
		multiRemoveEventListener(this.el.itemsButtons, 'mouseenter', this.events._onItemEnter);
		multiRemoveEventListener(this.el.items, 'mouseleave', this.events._onItemLeave);

		ResizeController.clear(this.controllers.resize);

		this.removeCursorListeners();
	}

	// specific listener add / remove

	addCursorListeners() {
		if (!this.controllers.animateCursor) {
			this.controllers.animateCursor = AnimationController.set({
				update: this.events._onAnimateCursorUpdate,
				render: this.events._onAnimateCursorRender
			});
		}
	}

	removeCursorListeners() {
		if (this.controllers.animateCursor) {
			AnimationController.clear(this.controllers.animateCursor);
		}
	}

	// listener methods

	_onItemEnter(e) {
		const $item = e.currentTarget.closest(`.${selector}__item`);
		if (mq('medium')) {
			this.setItemActive($item);
		}
	}

	_onItemLeave() {
		this.setInactive();
		this.clearActiveItemBounds();
	}

	_onResizeUpdate() {
		this.throttled.resize();
	}

	_onAnimateCursorUpdate() {
		return this.measureCursor();
	}

	_onAnimateCursorRender() {
		this.renderCursor();
	}

	// move cursor

	measureCursor() {
		const { scrollX, scrollY } = CursorController.coords;
		let isIn = false;
		if (this.el.activeItem && scrollX > this.bounds.section.left && scrollX < this.bounds.section.right
			&& scrollY > this.bounds.section.top && scrollY < this.bounds.section.bottom) {
			isIn = true;
		}

		this.flags.isCursorOver = isIn;

		if (isIn || (!isIn && this.flags.isCursorActive)) {
			return true;
		}
	}

	renderCursor() {
		if (this.flags.isInteractionReady && this.flags.isCursorOver) {
			this.cursor.moveTo(CursorController.coords.scrollX - this.bounds.section.left, CursorController.coords.scrollY - this.bounds.section.top);
		}
	}

	// set activated / deactivated states

	setItemActive($item) {
		if (this.el.activeItem !== $item) {
			const itemIndex = $item.dataset.index;

			// hide last image
			const $lastCursorImage = this.el.activeCursorImage;
			if ($lastCursorImage) {
				$lastCursorImage.classList.remove('is-active');
			}

			// define current item and image
			this.el.activeItem = $item;
			this.el.activeCursorImage = this.el.cursorImages[itemIndex];

			// activate current item, image, and general section (reflecting rollover state)
			this.el.section.classList.add('is-active');
			this.el.activeItem.classList.add('is-active');
			this.el.activeCursorImage.classList.add('is-active');

			// measure current item bounds for cursor
			this.measureItemBounds(this.el.activeItem);
		}
	}

	setInactive() {
		if (this.el.activeItem) {
			this.el.section.classList.remove('is-active');

			this.el.activeItem.classList.remove('is-active');
			this.el.activeItem = null;

			this.el.activeCursorImage.classList.remove('is-active');
			this.el.activeCursorImage = null;
		}
	}

	// measure resize

	measureResize() {
		if (mq('medium')) {
			this.addCursorListeners();
		} else {
			this.removeCursorListeners();
		}

		this.splitNames();
		this.bounds.section = getRootOffsetBounds(this.el.section);
		this.measureItemBounds(this.el.activeItem);
	}

	// measure active item container bounds

	measureItemBounds($item) {
		this.bounds.activeItem = getRootOffsetBounds($item);
	}

	clearActiveItemBounds() {
		this.bounds.activeItem = null;
	}

	// split guest names (for underline)

	splitNames() {

		// split words into individual spans

		for (let i = 0; i < this.splits.length; i++) {
			const split = this.splits[i];
			split.revert();
		}

		this.splits = [];

		for (let i = 0; i < this.el.items.length; i++) {
			const $item = this.el.items[i];

			// split name into individual line divs

			const $guestName = $item.querySelector(`.${selector}__guest-name-desktop`);
			const split = new SplitText($guestName, { type: 'lines', linesClass: 'line' });
			this.splits.push(split);

			// move guest image to left of first line

			const $guestThumb = $item.querySelector(`.${selector}__guest-thumb`);
			if ($guestThumb) {
				const itemWidth = $item.offsetWidth;
				const lineOffset = (itemWidth - split.lines[0].offsetWidth) / 2;
				const thumbOffset = Math.round(itemWidth - lineOffset);
				$guestThumb.style.right = `${thumbOffset}px`;
			}
		}
	}

	// destroy

	destroy() {
		this.removeEventListeners();
	}

}
